import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { ProjectAttachmentForm } from "../ProjectAttachmentForm/ProjectAttachmentForm";
import { useProjectAttachmentMutations } from "../api/useProjectAttachmentMutations";
import { useAttachmentTypeOptions } from "../api/useAttachmentTypeOptions";
import { useAttachmentTypeHolders } from "../api/useAttachmentTypeHolders";
import { attachmentSaveErrorText } from "../api/attachmentSaveError";
import {
  emptyProjectAttachmentForm,
  type ProjectAttachmentFormValues,
} from "../types";

const FORM_ID = "project-attachment-form";

export interface NewProps {
  projectId: string;
}

const New = ({ projectId }: NewProps): JSX.Element => {
  const navigate = useNavigate();
  const { create, uploadFile, archive } = useProjectAttachmentMutations();
  const { options } = useAttachmentTypeOptions();
  // Hide attachment types an active attachment already uses (uniqueness per
  // project). Types held only by archived attachments stay listed; picking one
  // fails with a message pointing at the Archived tab.
  const holders = useAttachmentTypeHolders(projectId);
  const { activeTypeIds } = holders;
  const typeOptions = useMemo(
    () => options.filter((o) => !activeTypeIds.has(o.value)),
    [options, activeTypeIds]
  );

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const handleSubmit = async (values: ProjectAttachmentFormValues) => {
    if (!values.file) return; // guarded in the form, defensive here
    setSubmitting(true);
    try {
      const result: any = await create(
        { attachmentTypeId: values.attachmentTypeId },
        { projectId }
      );
      const newId = result?.data?.id;
      try {
        await uploadFile(newId, values.file);
      } catch (uploadErr) {
        // The record exists but has no file. Deleting a file-less attachment
        // discards it server-side, freeing its type for the retry. Best effort:
        // the upload failure is what the user needs to see.
        await archive(String(newId)).catch(() => undefined);
        throw uploadErr;
      }
      navigate("..", {
        relative: "path",
        state: { toast: { variant: "success", text: "Attachment saved" } },
      });
    } catch (err) {
      setToast({
        variant: "danger",
        text: attachmentSaveErrorText(err, values.attachmentTypeId, holders),
      });
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Add an Attachment"
        formId={FORM_ID}
        onCancel={() => navigate("..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ProjectAttachmentForm
          id={FORM_ID}
          mode="new"
          initialValues={emptyProjectAttachmentForm}
          typeOptions={typeOptions}
          currentFilename={null}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
